import { type KeyboardEvent, type ReactElement, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { UiIcon, type UiIconName } from './icons';
import { Popover } from './overlays';

/**
 * The scope switcher of the top bar (V-LIB-SHELL, D509; audit consoles F12): the pill the Admin draws as "Escopo ·
 * Global" — an outlined header button with a glyph and a chevron that opens a popover — as the ONE pattern for "which
 * market / store / fleet am I looking at". Before this the PDV and the Merchant had a button that NAVIGATED (to a picker
 * screen) and the Fleet and Agent a native `<select>` that changed the scope in place. It always changes the scope in
 * place and never navigates.
 *
 *   <ScopeSwitcher label={t('header.scope')} value={marketName} options={markets} selectedId={marketId} onSelect={setMarket} />
 *
 * Two bodies: pass `options` (+ `selectedId`, `onSelect`) for a flat single-choice list (a `menu` of `menuitemradio`
 * rows — ArrowUp/Down move, Escape closes, the choice closes the menu and focus returns to the pill), or `children` — a
 * render function receiving `close` — for a richer body such as the Admin's country → city columns. Pair it with
 * `ConsoleShell`'s `scope` slot.
 */
export interface ScopeOption {
  readonly id: string;
  readonly label: string;
  readonly icon?: UiIconName;
  /** A second line under the label (a city's country, a store's address). */
  readonly description?: string;
}

export interface ScopeSwitcherProps {
  /** The dimension's name ("Scope", "Store", "Market"); shown before the value on the pill. */
  readonly label: string;
  /** The current choice, already translated ("Global", "São Paulo"). */
  readonly value: string;
  readonly icon?: UiIconName;
  readonly options?: readonly ScopeOption[];
  readonly selectedId?: string;
  readonly onSelect?: (id: string) => void;
  /** Custom popover body; receives `close`. Used instead of `options`. */
  readonly children?: (close: () => void) => ReactNode;
  /** Popover width in px (default 280). */
  readonly width?: number;
}

export function ScopeSwitcher({ label, value, icon = 'globe', options, selectedId, onSelect, children, width = 280 }: ScopeSwitcherProps): ReactElement {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const close = useCallback((): void => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  // Keyboard users land on the current choice (or the first one) when the menu opens.
  useEffect(() => {
    if (!open || options === undefined) return undefined;
    const t = window.setTimeout(() => {
      const menu = wrapRef.current?.querySelector<HTMLElement>('[role="menu"]');
      (menu?.querySelector<HTMLElement>('[aria-checked="true"]') ?? menu?.querySelector<HTMLElement>('[role="menuitemradio"]'))?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, options]);

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (options === undefined || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return;
    const items = Array.from(wrapRef.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"]') ?? []);
    const i = items.indexOf(document.activeElement as HTMLElement);
    e.preventDefault();
    items[e.key === 'ArrowDown' ? Math.min(i + 1, items.length - 1) : Math.max(i - 1, 0)]?.focus();
  };

  return (
    <div className="vello-scope-switcher" ref={wrapRef} onKeyDown={onMenuKey}>
      <button ref={buttonRef} type="button" className="vello-header-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <UiIcon name={icon} size={14} />
        <span className="vello-scope-switcher__text">
          <span className="vello-scope-switcher__label">{label} · </span>
          {value}
        </span>
        <span className="vello-header-btn__chevron">
          <UiIcon name="chevronDown" size={13} />
        </span>
      </button>
      <Popover open={open} onClose={close} width={width} top={50} role="menu" ariaLabel={label}>
        {options !== undefined ? (
          <>
            <div className="vello-menu__heading">{label}</div>
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                className="vello-menu__item"
                role="menuitemradio"
                aria-checked={o.id === selectedId}
                onClick={() => {
                  onSelect?.(o.id);
                  close();
                }}
              >
                {o.icon !== undefined && <UiIcon name={o.icon} size={16} />}
                <span className="vello-scope-switcher__option">
                  {o.label}
                  {o.description !== undefined && <span className="vello-scope-switcher__description">{o.description}</span>}
                </span>
                {o.id === selectedId && <UiIcon name="check" size={14} />}
              </button>
            ))}
          </>
        ) : (
          children?.(close)
        )}
      </Popover>
    </div>
  );
}
